"use client";

import React, { useEffect } from "react";
import Container from "@mui/material/Container";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import Alert from "@mui/material/Alert";
import { School } from "@/types/school";
import GoogleSignInButton from "@/components/shared/ui/GoogleSignInButton";
import { CircularProgress } from "@mui/material";
import { useT } from "@/i18n/useT";
import { useAuth } from "@/lib/useAuth";
import { useNavigation } from "@/lib/navigation";

interface SchoolLandingPageProps {
  school: School | null;
  schoolSlug: string | null;
  loading: boolean;
  error: string | null;
  onLogin: () => void;
}

/**
 * School-specific landing page for users visiting {school-slug}.{PLATFORM_NAME}.com
 */
export default function SchoolLandingPage({
  school,
  schoolSlug,
  loading,
  error,
  onLogin,
}: SchoolLandingPageProps) {
  const { user } = useAuth();
  const navigation = useNavigation();

  useEffect(() => {
    if (user) {
      navigation.navigateTo("/courses");
    }
  }, [user, navigation]);

  const t = useT();
  if (loading) {
    return (
      <Container maxWidth="md">
        <Box textAlign="center" py={8}>
          <Typography variant="h6">{t("schoolHome.loading")}</Typography>
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight="50vh"
          >
            <CircularProgress />
          </Box>
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md">
        <Box py={8}>
          <Alert severity="error" sx={{ mb: 4 }}>
            {error}
          </Alert>
          <Box textAlign="center">
            <Typography variant="h6" gutterBottom>
              {t("schoolHome.notFound")}
            </Typography>

            <Button variant="outlined" href="/" sx={{ mt: 2 }}>
              {t("schoolHome.goBackHome")}
            </Button>
          </Box>
        </Box>
      </Container>
    );
  }

  // If no school found, show generic landing page with claim option
  if (!school && schoolSlug && !loading) {
    return (
      <Container maxWidth="md">
        <Box textAlign="center" py={8}>
          <Typography variant="h3" component="h1" gutterBottom>
            {t("schoolHome.welcome")}{" "}
            {schoolSlug.charAt(0).toUpperCase() +
              schoolSlug.slice(1).replace(/-/g, " ")}
          </Typography>
          <Typography variant="h6" color="text.secondary" component={"p"}>
            {t("schoolHome.thisAddressCouldBeYours")}
          </Typography>
          <Typography color="text.secondary" component={"p"}>
            {t("schoolHome.claimItPrompt")}
          </Typography>
          <Stack
            direction="row"
            spacing={2}
            justifyContent="center"
            sx={{ mt: 3 }}
          >
            <GoogleSignInButton onClick={onLogin} />
          </Stack>
        </Box>
      </Container>
    );
  } else if (school) {
    return (
      <Container maxWidth="lg">
        {/* School Header */}
        <Box textAlign="center" pt={6}>
          <Typography
            variant="h1"
            component="h1"
            gutterBottom
            sx={{
              fontSize: { xs: "2.5rem", md: "3.5rem" },
              fontWeight: "bold",
              mb: 2,
            }}
          >
            {school.name}
          </Typography>

          <Box textAlign="center">
            <Typography variant="h4" gutterBottom>
              {t("schoolHome.readyToBegin")}
            </Typography>
            <Typography
              variant="body1"
              color="text.secondary"
              component={"p"}
              sx={{ mb: 4 }}
            >
              {t("schoolHome.signInToAccessCourses", {
                schoolName: school.name,
              })}
            </Typography>
            <GoogleSignInButton onClick={onLogin} />
          </Box>
        </Box>
      </Container>
    );
  }
}
